<template>
  <div>
    <NavBar />
    <AuthBySessionExpired />
    <b-container>
      <b-row align-v="center" class="mb-3">
        <b-col cols="auto">
          <v-btn class="btn-return" @click="$router.go(-1)">
            <i class="fas fa-arrow-left mr-2 text-twenty"></i>
            VOLTAR
          </v-btn>
        </b-col>
        <b-col>
          <h1 class="title-pages">
            CONFIGURAÇÃO DE IMPRESSÃO COM AS INFORMAÇÕES DO RESIDENTE
          </h1>
        </b-col>
      </b-row>

      <b-overlay :show="loading" opacity="0.99">
        <!-- ── Seção: Configuração de campos para impressão ───── -->
        <b-card class="mb-3">
          <h6 class="title-pages mb-3">CAMPOS PADRÃO PARA IMPRESSÃO</h6>
          <p class="worksansr mb-3" style="font-size: 13px; color: #666">
            Defina quais seções e campos aparecem por padrão ao imprimir
            relatórios que o residente vai no cabeçalho.
          </p>

          <div
            v-for="section in printSections"
            :key="section.key"
            class="mb-1"
            style="
              border: 1px solid #e9ecef;
              border-radius: 6px;
              overflow: hidden;
            "
          >
            <!-- Cabeçalho da seção -->
            <div
              class="d-flex align-items-center px-3 py-2"
              style="background: #f8f9fa; border-bottom: 1px solid #e9ecef"
            >
              <input
                type="checkbox"
                :id="`ps-${section.key}`"
                v-model="section.selected"
                @change="handleSectionToggle(section)"
                style="
                  width: 16px;
                  height: 16px;
                  cursor: pointer;
                  accent-color: #4e9130;
                  flex-shrink: 0;
                "
              />
              <label
                :for="`ps-${section.key}`"
                class="worksansr mb-0 ml-2 flex-grow-1"
                style="
                  font-size: 14px;
                  font-weight: 600;
                  cursor: pointer;
                  color: #343a40;
                "
              >
                {{ section.label }}
              </label>

              <!-- Expandir campos — só se a seção tiver campos granulares -->
              <button
                v-if="section.fields && section.fields.length > 0"
                type="button"
                @click="section.expanded = !section.expanded"
                style="
                  background: none;
                  border: none;
                  cursor: pointer;
                  color: #6c757d;
                  padding: 0 4px;
                  font-size: 12px;
                  display: flex;
                  align-items: center;
                  gap: 4px;
                "
              >
                <span class="worksansr" style="font-size: 11px">
                  {{
                    section.expanded ? "ocultar campos" : "configurar campos"
                  }}
                </span>
                <i
                  :class="
                    section.expanded
                      ? 'fas fa-chevron-up'
                      : 'fas fa-chevron-down'
                  "
                ></i>
              </button>
            </div>

            <!-- Lista de campos -->
            <div
              v-if="
                section.expanded && section.fields && section.fields.length > 0
              "
            >
              <!-- Marcar / Desmarcar todos -->
              <div
                class="d-flex px-3 py-1"
                style="background: #fff; border-bottom: 1px solid #f1f1f1"
              >
                <button
                  type="button"
                  class="worksansr"
                  style="
                    background: none;
                    border: none;
                    padding: 0;
                    font-size: 11px;
                    color: #4e9130;
                    cursor: pointer;
                    margin-right: 12px;
                  "
                  @click="selectAllFields(section, true)"
                >
                  Marcar todos
                </button>
                <button
                  type="button"
                  class="worksansr"
                  style="
                    background: none;
                    border: none;
                    padding: 0;
                    font-size: 11px;
                    color: #dc3545;
                    cursor: pointer;
                  "
                  @click="selectAllFields(section, false)"
                >
                  Desmarcar todos
                </button>
              </div>

              <!-- Campos individuais -->
              <div
                v-for="field in section.fields"
                :key="field.key"
                class="d-flex align-items-center px-4 py-1"
                style="border-bottom: 1px solid #f8f9fa"
              >
                <input
                  type="checkbox"
                  :id="`pf-${section.key}-${field.key}`"
                  v-model="field.selected"
                  :disabled="!section.selected"
                  style="
                    width: 14px;
                    height: 14px;
                    cursor: pointer;
                    accent-color: #4e9130;
                    flex-shrink: 0;
                  "
                />
                <label
                  :for="`pf-${section.key}-${field.key}`"
                  class="worksansr mb-0 ml-2"
                  :style="{
                    fontSize: '13px',
                    cursor: section.selected ? 'pointer' : 'not-allowed',
                    color: section.selected ? '#495057' : '#adb5bd',
                  }"
                >
                  {{ field.label }}
                </label>
              </div>
            </div>
          </div>
        </b-card>

        <errors-messages :errors="errors" />

        <b-row align-h="end" class="mb-4">
          <b-col cols="auto">
            <confirmation-dialog
              message-title="CONFIRMAÇÃO"
              message-body="TEM CERTEZA QUE DESEJA SALVAR?"
              open-class="btn-save"
              open-text="SALVAR"
              openLeftIcon="fa-check"
              @yes="save"
            />
          </b-col>
        </b-row>
      </b-overlay>
    </b-container>
  </div>
</template>

<script>
import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";
import ConfirmationDialog from "../templates/Components/ConfirmationDialog";
import api from "../services/api";
import toast from "../lib/ToastNotification";

// Estrutura padrão — usada quando não há nada salvo no banco ainda
const DEFAULT_PRINT_SECTIONS = [
  {
    key: "general",
    label: "Geral",
    selected: true,
    expanded: false,
    fields: [
      { key: "name", label: "Nome", selected: true },
      {
        key: "medical_record_number",
        label: "Número de Prontuário",
        selected: true,
      },
      { key: "gender_id", label: "Gênero", selected: true },
      {
        key: "date_of_birth",
        label: "Data de Nascimento / Idade",
        selected: true,
      },
      { key: "status_type_id", label: "Status", selected: false },
      { key: "phone", label: "Telefone", selected: false },
      {
        key: "degree_of_dependence_id",
        label: "Grau de Dependência",
        selected: true,
      },
      { key: "type_of_food", label: "Tipo de Alimentação", selected: false },
      { key: "pathology", label: "Patologia (Doença)", selected: false },
      {
        key: "miscellaneous_allergies",
        label: "Alergias Diversas",
        selected: false,
      },
      { key: "nickname", label: "Apelido", selected: false },
      { key: "naturalness", label: "Naturalidade", selected: false },
      { key: "marital_status_id", label: "Estado Civil", selected: true },
      { key: "religion", label: "Religião", selected: false },
      { key: "profession", label: "Profissão", selected: false },
      { key: "schooling", label: "Escolaridade", selected: false },
      { key: "fathers_name", label: "Nome do Pai", selected: false },
      { key: "mothers_name", label: "Nome da Mãe", selected: false },
      { key: "spouse", label: "Cônjuge", selected: false },
      { key: "comments", label: "Observações", selected: false },
      { key: "imc", label: "IMC", selected: false },
      { key: "diagnosis", label: "Diagnósticos", selected: false },
      { key: "health_plan", label: "Plano de saúde", selected: false },
      {
        key: "health_plan_plan",
        label: "Plano de saúde (Plano)",
        selected: false,
      },
      {
        key: "health_plan_registration",
        label: "Plano de saúde (Matrícula)",
        selected: false,
      },
      { key: "hospital", label: "Hospital", selected: false },
      {
        key: "hospital_address",
        label: "Endereço (Hospital)",
        selected: false,
      },
      { key: "ambulance", label: "Ambulância", selected: false },
      {
        key: "healthcare_professionals",
        label: "Profissionais de saúde",
        selected: false,
      },
      { key: "diets", label: "Dietas", selected: false },
    ],
  },
  {
    key: "address",
    label: "Endereço",
    selected: true,
    expanded: false,
    fields: [
      { key: "cep", label: "CEP", selected: true },
      { key: "address", label: "Endereço", selected: true },
      { key: "number_address", label: "Número", selected: true },
      { key: "neighborhood", label: "Bairro", selected: true },
      { key: "city", label: "Cidade", selected: true },
      { key: "uf_id", label: "Estado", selected: true },
      { key: "complement", label: "Complemento", selected: false },
      { key: "reference", label: "Referência", selected: false },
    ],
  },
  {
    key: "docs",
    label: "Documentos",
    selected: false,
    expanded: false,
    fields: [
      { key: "cpf", label: "CPF", selected: true },
      { key: "rg", label: "RG", selected: true },
      { key: "issuing_body", label: "Órgão Expedidor", selected: true },
      { key: "date_of_issue", label: "Data de Emissão", selected: true },
      { key: "SUS_card_number", label: "Número do Cartão SUS", selected: true },
      {
        key: "INSS_benefit_number",
        label: "Número do Benefício INSS",
        selected: false,
      },
      {
        key: "type_of_INSS_benefit",
        label: "Tipo de Benefício INSS",
        selected: false,
      },
      {
        key: "inss_benefit_status_id",
        label: "Situação do Benefício INSS",
        selected: false,
      },
      {
        key: "INSS_benefit_amount",
        label: "Valor do Benefício INSS",
        selected: false,
      },
      {
        key: "INSS_update_date",
        label: "Data de Atualização INSS",
        selected: false,
      },
    ],
  },
  {
    key: "agreement",
    label: "Convênios",
    selected: false,
    expanded: false,
    fields: [
      {
        key: "type_of_agreement_id",
        label: "Tipo de Convênio",
        selected: true,
      },
      { key: "covenant_name", label: "Nome do Convênio", selected: true },
      { key: "number", label: "Nº Carteirinha", selected: true },
      { key: "type_plan_name", label: "Tipo/Nome do Plano", selected: true },
      { key: "exchange_phone", label: "Telefone da Central", selected: true },
      { key: "name", label: "Nome do Titular", selected: true },
      {
        key: "type_of_customer_id",
        label: "Grau de Parentesco",
        selected: true,
      },
      { key: "phone", label: "Telefone", selected: true },
      { key: "cell_phone", label: "Celular", selected: false },
      { key: "note", label: "Observação", selected: false },
      { key: "cep", label: "CEP", selected: false },
      { key: "address", label: "Endereço", selected: false },
      { key: "number_address", label: "Número", selected: false },
      { key: "neighborhood", label: "Bairro", selected: false },
      { key: "city", label: "Cidade", selected: false },
      { key: "uf_id", label: "Estado", selected: false },
      { key: "reference", label: "Referência", selected: false },
      { key: "complement", label: "Complemento", selected: false },
    ],
  },
  {
    key: "hosting",
    label: "Hospedagem",
    selected: true,
    expanded: false,
    fields: [
      {
        key: "type_of_hosting_id",
        label: "Tipo de Hospedagem",
        selected: true,
      },
      { key: "date_of_reception", label: "Data da Hospedagem", selected: true },
      { key: "bed_id", label: "Leito", selected: true },
      {
        key: "income_monthly_fee",
        label: "Valor da Mensalidade",
        selected: false,
      },
      {
        key: "reason_for_the_reception",
        label: "Motivo da Hospedagem",
        selected: false,
      },
      {
        key: "date_end_contract",
        label: "Vencimento do Contrato",
        selected: false,
      },
    ],
  },
];

export default {
  name: "ConfigResidentPrintPrescriptionLote",

  components: {
    NavBar,
    AuthBySessionExpired,
    ErrorsMessages,
    ConfirmationDialog,
  },

  data() {
    return {
      errors: [],
      loading: false,
      // Deep clone para não mutar o DEFAULT durante a sessão
      printSections: JSON.parse(JSON.stringify(DEFAULT_PRINT_SECTIONS)),
    };
  },

  created() {
    this.retrieve();
  },

  methods: {
    async retrieve() {
      await this.get();
    },

    async get() {
      this.loading = true;
      try {
        const response = await api.get(
          "/rest-home/settings/config-resident-print-prescription-lote"
        );
        const { error, errors, print_sections } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          // Se o banco já tem configuração salva, faz merge com o padrão
          // O merge garante que campos novos adicionados futuramente apareçam
          if (print_sections) {
            let saved = JSON.parse(print_sections);

            // Se ainda for string após o primeiro parse, parseia novamente
            if (typeof saved === "string") {
              saved = JSON.parse(saved);
            }

            this.printSections = this.mergeSections(
              DEFAULT_PRINT_SECTIONS,
              saved
            );
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    /**
     * Merge entre a estrutura padrão (fonte da verdade de campos disponíveis)
     * e os valores salvos no banco (fonte da verdade de selected/expanded).
     * Assim, novos campos adicionados ao DEFAULT aparecem automaticamente
     * sem quebrar configurações já salvas.
     */
    mergeSections(defaults, saved) {
      return defaults.map((defaultSection) => {
        const savedSection = saved.find((s) => s.key === defaultSection.key);
        if (!savedSection) return { ...defaultSection };

        const mergedFields = defaultSection.fields.map((defaultField) => {
          const savedField = savedSection.fields
            ? savedSection.fields.find((f) => f.key === defaultField.key)
            : null;
          return savedField
            ? { ...defaultField, selected: savedField.selected }
            : { ...defaultField };
        });

        return {
          ...defaultSection,
          selected: savedSection.selected,
          expanded: false, // sempre começa recolhido
          fields: mergedFields,
        };
      });
    },

    async save() {
      try {
        this.loading = true;

        let { printSections } = this;

        // Serializa só o necessário: key, selected e fields com key+selected
        const sectionsToSave = printSections.map((s) => ({
          key: s.key,
          selected: s.selected,
          fields: s.fields
            ? s.fields.map((f) => ({ key: f.key, selected: f.selected }))
            : [],
        }));

        const data = {
          print_sections: JSON.stringify(sectionsToSave),
        };

        const response = await api.patch(
          "/rest-home/settings/config-resident-print-prescription-lote",
          data
        );

        const { error, errors, message } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          this.errors = [];
          toast(message);
          this.retrieve();
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },

    handleSectionToggle(section) {
      if (!section.selected && section.fields) {
        section.fields.forEach((f) => {
          f.selected = false;
        });
      }
    },

    selectAllFields(section, value) {
      if (section.fields) {
        section.fields.forEach((f) => {
          f.selected = value;
        });
      }
    },
  },
};
</script>